import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Loader2, Pencil } from "lucide-react";
import { api } from "../lib/config";
import { toast } from "sonner";

export default function EditBookingDialog({ open, onOpenChange, booking, onUpdated }) {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !booking) return;
    setForm({
      name: booking.name || "",
      email: booking.email || "",
      phone: booking.phone || "",
      address: booking.address || "",
      preferred_date: booking.preferred_date || "",
      preferred_time: booking.preferred_time || "",
      square_feet: booking.square_feet || "",
      price: booking.price ?? "",
      notes: booking.notes || "",
    });
  }, [open, booking]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === "" || v == null) continue;
        if (k === "square_feet") body[k] = parseInt(v) || 0;
        else if (k === "price") body[k] = parseFloat(v) || 0;
        else body[k] = v;
      }
      const { data } = await api.patch(`/admin/bookings/${booking.booking_id}`, body);
      toast.success("Booking updated");
      onUpdated && onUpdated(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSubmitting(false); }
  };

  if (!booking) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="edit-booking-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-[#047857]" /> Edit · {booking.service_name}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-1 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-name" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="edit-phone" /></div>
          </div>
          <div><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="edit-email" /></div>
          <div><Label>Address</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="edit-address" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Date</Label><Input type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} data-testid="edit-date" /></div>
            <div><Label>Time</Label><Input value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} data-testid="edit-time" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Sq ft</Label><Input type="number" value={form.square_feet} onChange={(e) => set("square_feet", e.target.value)} data-testid="edit-sqft" /></div>
            <div><Label>Price ($)</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="edit-price" /></div>
          </div>
          <div><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="edit-notes" /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={submitting} data-testid="edit-submit-btn"
            className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
